import React from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import { t } from 'i18next';
import { DataCard, Icon } from '@brighterion/br-ui';
import DashOrEllipText from 'Components/DashOrEllipText';
import { NumberService } from 'Services';
import { timeZone } from '@brighterion/utils/date-timezone';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';

import styles from './OverviewContainer.css';

export type OverviewContainerProps = {
    boldedText?: boolean;
    children?: React.ReactNode;
    dataTestId?: string;
    deployment?: Deployment;
    leftContainerClassName?: string;
    leftData?: React.ComponentProps<typeof DataCard>[];
    leftTitle?: React.ReactNode;
    rightData?: React.ComponentProps<typeof DataCard>[];
};

export const OverviewContainer: React.FC<OverviewContainerProps> = ({
    boldedText,
    children,
    dataTestId,
    deployment,
    leftContainerClassName,
    leftData,
    leftTitle,
    rightData
}) => {
    if (children) {
        return <>{children}</>;
    }
    if (deployment) {
        const { locale } = useTemplateStore.getState().template;
        const cells = [
            {
                label: t('Status'),
                value:
                    deployment.status === 'failed' ? (
                        <div className={styles.statusContainer}>
                            <Icon name='small-error' />
                            <span className={styles.statusFailedLabel}>
                                {t('Deployment')} {t(deployment.status)}
                            </span>
                        </div>
                    ) : (
                        t(deployment.status)
                    )
            },
            {
                label: t('Scheduled at'),
                value: deployment.scheduledAt
                    ? new Date(deployment.scheduledAt).toLocaleString(locale, {
                          timeZone,
                          dateStyle: 'short',
                          timeStyle: 'short'
                      })
                    : '--'
            },
            {
                label: t('Scheduled for'),
                value: deployment.scheduledFor
                    ? new Date(deployment.scheduledFor).toLocaleString(locale, {
                          timeZone,
                          dateStyle: 'short',
                          timeStyle: 'short'
                      })
                    : '--'
            },
            {
                label: t('Scheduled by'),
                value: deployment.scheduledBy
            },
            {
                label: t('Comment'),
                value: deployment.comment
            },
            {
                label: t('New Global Rules'),
                value: deployment.summary.acquirerSummary.addedRules
            },
            {
                label: t('Modified Global Rules'),
                value: deployment.summary.acquirerSummary.modifiedRules
            },
            {
                label: t('New Tenant Groups'),
                value: deployment.summary.tenantGroupSummary.added
            },
            {
                label: t('Modified Tenant Groups'),
                value: deployment.summary.tenantGroupSummary.modified
            },
            {
                label: t('Execution Order Changed'),
                value: deployment.summary.acquirerSummary.modifiedExecutionOrder ? 'Yes' : 'No'
            }
        ];
        return (
            <dl className={styles.scheduled} title={leftTitle?.toString()} role='group'>
                {cells.map((cell) => {
                    /** Empty string will be converted in -- */
                    if (cell.value === '') {
                        cell.value = '--';
                    }
                    return (
                        <div className={styles.cell} key={_.uniqueId('cell_')}>
                            <dt>{cell.label}</dt>
                            <dd>
                                <b>{cell.value}</b>
                            </dd>
                        </div>
                    );
                })}
            </dl>
        );
    }
    return (
        <>
            <div className={clsx(styles.left, leftContainerClassName)}>
                {(leftData as React.ComponentProps<typeof DataCard>[])?.map((o) => {
                    const formattedData =
                        typeof o?.data === 'number' ? NumberService.getLocaleNumber(o?.data) : o?.data;
                    const data = (o as any).noEllipsis ? (
                        formattedData
                    ) : (
                        <DashOrEllipText rawValue={formattedData as string} bold={boldedText} />
                    );
                    return (
                        <DataCard
                            dataBoldFont
                            dataTestId={`${dataTestId}-leftcard`}
                            className={clsx(styles.leftCard, o?.className)}
                            key={o?.label}
                            data={data}
                            label={o?.label}
                        />
                    );
                })}
            </div>
            <div className={styles.right}>
                {rightData?.map((o) => {
                    return (
                        <DataCard
                            dataTestId={`${dataTestId}-rightcard`}
                            key={o?.label}
                            data={o?.data}
                            label={o?.label}
                        />
                    );
                })}
            </div>
        </>
    );
};
